import {IColumnMetadata} from "@/features/application/api/applicationModels";
import React, {FC, SyntheticEvent, useEffect, useState} from "react";
import {Button, Dropdown, Icon, Input, Label, Popup} from "semantic-ui-react";
import {
    ComparisonMethodType,
    IMonitorParameterOption
} from "@/features/monitors/api/monitorsModels";
import {SemanticValueType} from "@/features/tasks/api/tasksModels";
import {IDataTableFilter, INumericalFilter} from "./DataTableModels";
import dataFormatUtil, { DataType } from "@/util/dataFormatUtil";
import {
    getAmountValidationErrorMessage,
    validateDataType
} from "@/features/monitors/components/MonitorConfigurationFormValidation";
import TimeOfDayPicker from "../TimeOfDayPicker";

interface IDataTableHeaderFilterNumber {
    //filters can also be set from outside the column filter itself, like clicking on an employee
    //in another component and then seeing just that employee in the grid.
    //The "applied" filters are controlled by a parent component.  When this component needs to update
    //the applied filters it does it via the parent by calling setFilters which is passed in as a prop here
    appliedFilter?: INumericalFilter;
    column: IColumnMetadata;
    setFilters: (
        filterColumnMetricCode: string,
        selectedValues: IDataTableFilter
    ) => void;
    labelPrefix?: string;
    labelSuffix?: string;
    comparisonOptions: IMonitorParameterOption[]; // TODO: get this to work for ComparisonMethodType.Between?
}

// Works for filtering DataType Number, Money, Percent, TimeOfDay, TimeSpan, and PrettyTimeSpan
// Works with Time as well, with additional logic in the filtering in index.tsx
const DataTableHeaderFilterNumerical: FC<IDataTableHeaderFilterNumber> = ({
    appliedFilter,
    column,
    setFilters,
    labelPrefix,
    labelSuffix,
    comparisonOptions
}) => {
    //Min/Max values for times
    const midnightMinutes = 0;
    const elevenFortyFivePmMinutes = (23 * 60) + 45;

    const comparisonDefault = ComparisonMethodType.GreaterThanOrEqual;
    //selected, but not necessarily applied filters
    const [filterComparison, setFilterComparison] =
        useState<ComparisonMethodType>(comparisonDefault);

    //a string is used here because it interacts with an html input
    const [filterAmount, setFilterAmount] = useState<string | undefined>();
    const [secondaryFilterAmount, setSecondaryFilterAmount] = useState<string | undefined>();

    const [isFilterOpen, setIsFilterOpen] = useState(false);

    const [isValidationFailed, setIsValidationFailed] = useState(false);

    useEffect(() => {
        if (appliedFilter) {
            setFilterComparison(appliedFilter.measure);
            setFilterAmount(
                dataFormatUtil.metricDataRawDataToDisplayValue(
                    appliedFilter?.amount,
                    column.dataType
                )
            );
            setSecondaryFilterAmount(dataFormatUtil.metricDataRawDataToDisplayValue(
                appliedFilter?.secondaryAmount,
                column.dataType
            ));
        } else {
            setFilterAmount(undefined);
            setSecondaryFilterAmount(undefined);
            setFilterComparison(comparisonDefault);
        }
    }, [appliedFilter, comparisonDefault, column.dataType]);

    const inputElement: JSX.Element = getInputForDataTypeAndComparisonMethodType(
        column.dataType,
        filterComparison,
        filterAmount,
        (newValue) => {
            const valueIsSet = (newValue !== undefined);

            setFilterAmount((valueIsSet ? newValue : undefined));

            const isValid = componentDataIsValid(
                column.dataType,
                column.decimalPlaces,
                filterComparison,
                newValue,
                secondaryFilterAmount
            );

            setIsValidationFailed(!isValid);

        },
        secondaryFilterAmount,
        (newSecondaryValue) => {
            const valueIsSet = (newSecondaryValue !== undefined);

            setSecondaryFilterAmount((valueIsSet ? newSecondaryValue : undefined));

            const isValid = componentDataIsValid(
                column.dataType,
                column.decimalPlaces,
                filterComparison,
                filterAmount,
                newSecondaryValue
            );

            setIsValidationFailed(!isValid);
        },
        labelPrefix,
        labelSuffix
    );

    const displayFilter = (column: IColumnMetadata) => {
        const numericalFilter = (
            <div style={{ maxHeight: "300px" }}>
                {column.label}
                <Dropdown
                    options={comparisonOptions}
                    selection
                    compact
                    style={{ marginLeft: "10px", marginRight: "10px" }}
                    onChange={(
                        _e: SyntheticEvent<HTMLElement, Event>,
                        { value }: { value?: SemanticValueType }
                    ) => {
                        setFilterComparison(value as ComparisonMethodType);
                    }}
                    value={filterComparison}
                />
                {inputElement}
                {isValidationFailed && (
                    <>
                        <br />
                        <Label
                            basic
                            color="red"
                            pointing="above"
                            style={{ marginTop: "5px", float: "right" }}
                        >
                            {getValidationMessage(column.dataType, filterAmount, secondaryFilterAmount, isValidationFailed)}
                        </Label>
                    </>
                )}
            </div>
        );

        return numericalFilter;
    };

    return (
        <Popup
            flowing
            trigger={
                <Icon
                    name="filter"
                    circular={true}
                    className="filterIcon"
                    size="small"
                    inverted={appliedFilter ? true : false}
                ></Icon>
            }
            open={isFilterOpen}
            onOpen={(event: React.MouseEvent<HTMLElement, MouseEvent>) => {
                // Stop propagation on onOpen stops propagation (sorting the column) when the popup icon is clicked
                event.stopPropagation();
                setIsFilterOpen(true);
            }}
            onClose={(event: React.MouseEvent<HTMLElement, MouseEvent>) => {
                // Stop propagation on onClose stops propagation (sorting the column) when the popup icon is clicked
                event.stopPropagation();
                setIsFilterOpen(false);
            }}
            onClick={(e: any) => {
                // Stop propagation on onClick stops propagation (sorting the column) when the popup body is clicked
                e.stopPropagation();
            }}
            //popperDependencies={[props.thresholdFilters.length]}
            position={"top center"}
            openOnTriggerMouseEnter={false}
            openOnTriggerClick={true}
            closeOnTriggerClick={true}
            closeOnDocumentClick={true}
            closeOnEscape={true}
            closeOnTriggerMouseLeave={false}
            style={{ maxHeight: "400px" }}
        >
            Filter by:
            <br />
            <br />
            {displayFilter(column)}
            <Button
                className="btn btn-blue-solid"
                onClick={() => {
                    const selectedFiltersSet = new Set<INumericalFilter>();

                    const unformattedValue =
                        dataFormatUtil.metricDataDisplayValueToRawData(
                            filterAmount,
                            column.dataType
                        );

                    let unformattedSecondaryValue: number | undefined = undefined;
                    let isValid = false;
                    if (filterComparison === ComparisonMethodType.Between) {
                        unformattedSecondaryValue = dataFormatUtil.metricDataDisplayValueToRawData(
                            secondaryFilterAmount,
                            column.dataType
                        ) as number | undefined;
                        isValid = componentDataIsValid(
                            column.dataType,
                            column.decimalPlaces,
                            filterComparison,
                            filterAmount,
                            secondaryFilterAmount);
                    }
                    else {
                        isValid = validateDataType(
                            unformattedValue,
                            column.dataType,
                            column.decimalPlaces
                        );
                    }

                    if (
                        isValid &&
                        filterComparison !== undefined &&
                        unformattedValue !== undefined
                    ) {
                        const filter = {
                            measure: filterComparison,
                            amount: unformattedValue as number,
                            secondaryAmount: unformattedSecondaryValue
                        };

                        selectedFiltersSet.add(filter);

                        setFilters(column.key, selectedFiltersSet);
                        setIsFilterOpen(false);
                    } else {
                        setIsValidationFailed(true);
                    }
                }}
                style={{ marginTop: "5px" }}
            >
                Apply
            </Button>
            {appliedFilter && (
                <Button
                    //remove existing filter
                    onClick={() => {
                        setIsFilterOpen(false);
                        setFilters(column.key, new Set());
                    }}
                    className="btn btn-blue"
                >
                    Remove
                </Button>
            )}
            <Button
                className="btn btn-blue"
                onClick={() => {
                    setIsFilterOpen(false);
                    //reset selected filter to the applied filters
                    setFilterComparison(
                        appliedFilter ? appliedFilter.measure : comparisonDefault
                    );
                    setFilterAmount(
                        appliedFilter ? appliedFilter.amount.toString() : undefined
                    );
                    setSecondaryFilterAmount(appliedFilter ? appliedFilter.secondaryAmount?.toString() : undefined);
                }}
            >
                Cancel
            </Button>
        </Popup>
    );

    function componentDataIsValid(
        dataType: DataType | undefined,
        decimalPlaces: number | undefined,
        comparisonType: ComparisonMethodType,
        filterAmountString: string | null | undefined,
        secondaryFilterAmountString: string | null | undefined
    ) {
        //Make sure that the primary filter value is valid as a number.
        if (filterAmountString === undefined || filterAmountString === null) {
            return false;
        }

        const parsedFilterAmount = dataFormatUtil.metricDataDisplayValueToRawData(
            filterAmountString,
            dataType
        ) as number | undefined | null;
        if (!validateDataType(
            parsedFilterAmount,
            dataType,
            decimalPlaces)) {
            return false;
        }

        if (comparisonType === ComparisonMethodType.Between) {
            //Make sure that the second value is valid as a number
            if (secondaryFilterAmountString === undefined || secondaryFilterAmountString === null) {
                return false;
            }

            const parsedSecondaryFilterAmount = dataFormatUtil.metricDataDisplayValueToRawData(
                secondaryFilterAmountString,
                dataType
            ) as number | undefined | null;
            if (!validateDataType(
                parsedSecondaryFilterAmount,
                dataType,
                decimalPlaces)) {
                return false;
            }

            //Make sure that the values are valid relative to one-another
            if (parsedFilterAmount! >= parsedSecondaryFilterAmount!) {
                return false;
            }
        }

        return true;
    }

    function getInputForDataTypeAndComparisonMethodType(
        dataType: DataType | undefined,
        comparisonMethodType: ComparisonMethodType,
        value: string | undefined,
        onValueChange: (newValue: string | undefined) => void,
        secondaryValue: string | undefined,
        onSecondaryValueChanged: (newSecondaryValue: string | undefined) => void,
        labelPrefix?: string,
        labelSuffix?: string) {

        const css: React.CSSProperties | undefined = (dataType === DataType.Time || dataType === DataType.TimeOfDay
            ? { width: "110px" }
            : { width: "80px" });
        if (comparisonMethodType === ComparisonMethodType.Between) {

            const greaterThanInput = getInputForDataType(
                dataType,
                value,
                onValueChange,
                css,
                labelPrefix,
                labelSuffix
            );

            const lessThanInput = getInputForDataType(
                dataType,
                secondaryValue,
                onSecondaryValueChanged,
                css,
                labelPrefix,
                labelSuffix
            );

            return <>{greaterThanInput} and {lessThanInput}</>;
        }
        else {
            return getInputForDataType(
                dataType,
                value,
                onValueChange,
                css,
                labelPrefix,
                labelSuffix);
        }
    }

    function getInputForDataType(
        dataType: DataType | undefined,
        value: string | undefined ,
        onValueChange: (newValue: string | undefined) => void,
        css: React.CSSProperties | undefined = undefined,
        labelPrefix?: string,
        labelSuffix?: string) {
        switch (dataType) {
            case DataType.Time:
            case DataType.TimeOfDay:
                const timeInMinutes = dataFormatUtil.metricDataDisplayValueToRawData(value, dataType) as number | undefined;

                return <TimeOfDayPicker
                    style={css}
                    onChange={(newValue) => onValueChange(newValue?.toString())}
                    valueInMinutes={timeInMinutes}
                    minMinutes={midnightMinutes}
                    maxMinutes={elevenFortyFivePmMinutes} />;
            default:
                return <Input labelPosition={labelSuffix ? "right" : "left"} type="text">
                    {labelPrefix ? <Label basic>{labelPrefix}</Label> : null}
                    <input
                        style={css}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const inputValue = e.target.value;
                            onValueChange(inputValue);
                        }}
                        value={value !== undefined && value !== null ? value.toString() : ""}
                    />
                    {labelSuffix ? <Label basic>{labelSuffix}</Label> : null}
                </Input>;
        }

    }

    function getValidationMessage(
        dataType: DataType | undefined,
        filterAmountString: string | undefined,
        secondaryFilterAmountString: string | undefined,
        validationFailed: boolean) {
        if (!validationFailed) {
            return undefined;
        }

        const parsedFilterAmount = dataFormatUtil.metricDataDisplayValueToRawData(
            filterAmountString,
            dataType
        ) as number | undefined;
        const parsedSecondaryFilterAmount = dataFormatUtil.metricDataDisplayValueToRawData(
            secondaryFilterAmountString,
            dataType
        ) as number | undefined;

        return getAmountValidationErrorMessage(
            dataType,
            filterComparison,
            parsedFilterAmount,
            parsedSecondaryFilterAmount
        );

    }
};

export default DataTableHeaderFilterNumerical;
